import { API, api } from "@/lib/api";

/* The chat has no name of its own – it is simply the house answering. Keys are prefixed hk_chat_*. */
const KEY = "hk_chat_session";
const LEGACY_KEY = "hk_husvert_session";

const makeId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, "");
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`.slice(0, 32);
};

/** Anonymous, device-local chat session id (8–64 chars as the API requires). Never contains personal data. */
export const getSessionId = () => {
  try {
    let id = localStorage.getItem(KEY) || localStorage.getItem(LEGACY_KEY);
    if (!id || id.length < 8) id = makeId();
    localStorage.setItem(KEY, id);
    localStorage.removeItem(LEGACY_KEY);
    return id;
  } catch {
    return makeId();
  }
};

export const resetSession = () => {
  try { localStorage.removeItem(KEY); } catch { /* private mode */ }
  return getSessionId();
};

export const fetchAssistantStatus = () => api.get("/public/assistent/status").then((r) => r.data);
export const fetchAssistantSession = (id) => api.get(`/public/assistent/sessions/${id}`).then((r) => r.data);
export const confirmAssistantDraft = (session_id, draft_id) => api.post("/public/assistent/confirm", { session_id, draft_id }).then((r) => r.data);
export const cancelAssistantDraft = (session_id, draft_id) => api.post("/public/assistent/cancel", { session_id, draft_id }).then((r) => r.data);

/**
 * POST + server-sent events. Calls `onEvent` for every `data:` frame:
 * start · delta{text} · tool_start{name,label} · card{card} · error{message} · done{message}
 */
export async function streamAssistantChat({ sessionId, message, onEvent, signal }) {
  const res = await fetch(`${API}/public/assistent/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify({ session_id: sessionId, message }),
    signal,
  });
  if (!res.ok) {
    let detail = "";
    try { detail = (await res.json())?.detail; } catch { /* non-json */ }
    const err = new Error(typeof detail === "string" && detail ? detail : `Tenesta svara med ${res.status}`);
    err.status = res.status;
    throw err;
  }
  if (!res.body) throw new Error("Nettlesaren støttar ikkje strauming.");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  const flush = (chunk) => {
    buf += chunk;
    let idx;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const data = frame.split("\n").filter((l) => l.startsWith("data:")).map((l) => l.slice(5).trim()).join("\n");
      if (!data) continue;
      try { onEvent(JSON.parse(data)); } catch { /* ignore malformed frame */ }
    }
  };
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    flush(decoder.decode(value, { stream: true }));
  }
  flush(decoder.decode());
}

/* ---------------------------------------------------------------- open-from-anywhere bus */
export const CHAT_OPEN_EVENT = "chat:open";
/** Opens the floating chat from any public page; `message` is sent as soon as the chat is ready. */
export const openChat = (message) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CHAT_OPEN_EVENT, { detail: { message: (message || "").trim() || null } }));
};

/* ------------------------------------------------------- small device-local memories */
const read = (key, fallback) => { try { return JSON.parse(localStorage.getItem(key) || "null") ?? fallback; } catch { return fallback; } };
const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ } };

const FORMS_KEY = "hk_chat_forms";
export const formWasSubmitted = (id) => read(FORMS_KEY, []).includes(id);
export const markFormSubmitted = (id) => { const arr = read(FORMS_KEY, []); if (!arr.includes(id)) write(FORMS_KEY, [...arr, id].slice(-40)); };

/** Thumbs up/down per assistant message – kept on the device only (no personal data leaves the browser). */
const RATING_KEY = "hk_chat_ratings";
export const getRating = (id) => read(RATING_KEY, {})[id] || null;
export const setRating = (id, value) => { const all = read(RATING_KEY, {}); if (value) all[id] = value; else delete all[id]; write(RATING_KEY, all); };

/** Whether the little «Lurer du på noko?» greeting next to the launcher has been shown on this device. */
const GREETED_KEY = "hk_chat_greeted";
export const wasGreeted = () => read(GREETED_KEY, false) === true;
export const markGreeted = () => write(GREETED_KEY, true);
